import { Head, Link } from '@inertiajs/react';
import { type CSSProperties, Fragment, type RefObject, useEffect, useRef, useState } from 'react';
import { FaPerson, FaPersonDress } from 'react-icons/fa6';
import { ArcadeShowcase } from '@/components/arcade/arcade-showcase';
import type { LandingsShow } from '@/types';
import DualPhotoSlide from './components/dual-photo-slide';
import type { LandingVariant } from './components/landing-variant';
import { MentorsSlide } from './components/mentors-slide';
import PrizeSlide from './components/prize-slide';
import { SantiagoPrizeSlide } from './components/santiago-prize-slide';
import { RotatingMate } from './components/rotating-mate';
import { RotatingSkull } from './components/rotating-skull';
import { ScheduleSlide } from './components/schedule-slide';
import { WireBus } from './components/wire-bus';
import { ParrotFiligree } from './components/parrot-filigree';
import { TelescopeHero } from '@/components/elt/telescope-hero';
import { GoldenGateSection } from '@/components/golden-gate/golden-gate-section';
import { YareMask } from './components/yare-mask';

// The scrolling landing of a tour stop: the port of `app/(public)/26-{ar,co,mx}/
// page-client.tsx` as one page (PORTING.md G5). Two axes vary and they are independent:
//
//   * the *look* — Bogotá and Ciudad de México wear the route group's `theme-ph26`
//     palette and set headings in `font-title`; Buenos Aires inverts the palette, drops
//     the theme classes and sets every heading in `font-bold font-title`, and paints the
//     marquee emphasis as outlined text rather than as a filled chip. That is `STYLES`,
//     keyed by the variant.
//   * the *copy and the data* — city, dates, hacker count, sponsor logos, whether the
//     applications are still open and which model spins in the hero. That is `EDITIONS`,
//     keyed by the slug.
const OUTLINE_STYLE: CSSProperties = {
  WebkitTextFillColor: 'transparent',
  WebkitTextStroke: '1.5px currentColor',
};

type SlideStyles = {
  hero: string;
  heroDates: string;
  applyButton: string;
  applySubLabel: string;
  chevron: string;
  peopleSection: string;
  marqueeHeading: string;
  marqueeTrack: string;
  marqueeChip: string | undefined;
  marqueeOutline: CSSProperties | undefined;
  sponsorSection: string;
  sponsorHeading: string;
  featuredRow: string;
  tourGrid: string;
  tourItem: string;
  arcadeSection: string;
};

const STYLES: Record<LandingVariant, SlideStyles> = {
  ph26: {
    hero: 'theme-ph26 selection-on-light relative min-h-svh w-full overflow-hidden bg-background text-primary md:h-dvh',
    heroDates: 'font-sans',
    applyButton:
      'inline-flex min-h-12 cursor-not-allowed flex-col items-center justify-center border-2 border-primary/60 bg-primary/40 px-6 py-2.5 text-center font-title text-background/70 text-base uppercase tracking-[0.12em] [animation:landingFadeIn_700ms_cubic-bezier(0.22,1,0.36,1)_420ms_both] md:min-h-14 md:px-10 md:text-xl',
    applySubLabel:
      'mt-0.5 font-mono text-[9px] text-background/75 uppercase tracking-[0.08em] md:text-[11px]',
    chevron:
      'font-title text-3xl text-primary/85 leading-none [animation:bounce_1.5s_ease-in-out_infinite]',
    peopleSection:
      'theme-ph26-light selection-on-light flex h-auto min-h-0 w-full items-start justify-center bg-background px-6 py-10 text-primary sm:px-10 md:h-dvh md:items-center md:py-0',
    marqueeHeading:
      'm-0 whitespace-nowrap font-title text-4xl text-primary uppercase leading-none sm:text-6xl md:text-7xl lg:text-8xl',
    marqueeTrack:
      'flex w-max flex-nowrap items-center gap-5 whitespace-nowrap px-4 font-title text-primary uppercase leading-none tracking-[0.08em]',
    marqueeChip: 'bg-primary px-3 py-1 text-background',
    marqueeOutline: undefined,
    sponsorSection:
      'theme-ph26-light selection-on-light flex w-full items-center justify-center bg-background px-6 py-16 text-primary sm:px-10 md:h-dvh md:py-0',
    sponsorHeading:
      'mt-4 font-title text-5xl uppercase leading-tight sm:text-6xl md:text-7xl',
    featuredRow:
      'flex transform-gpu flex-wrap items-center justify-center gap-8 transition duration-700 ease-out will-change-transform',
    tourGrid: 'flex flex-wrap justify-center gap-8',
    tourItem:
      'flex basis-[calc(50%-1rem)] transform-gpu items-center justify-center transition duration-700 ease-out will-change-transform sm:basis-[calc(33.333%-1.334rem)] lg:basis-[calc(20%-1.6rem)]',
    arcadeSection:
      'theme-ph26-light relative min-h-svh w-full overflow-hidden bg-background',
  },
  ar: {
    hero: 'selection-on-light relative min-h-svh w-full overflow-hidden bg-primary text-background md:h-dvh',
    heroDates: 'font-bold',
    applyButton:
      'inline-flex min-h-12 cursor-not-allowed flex-col items-center justify-center border-2 border-background/60 bg-background/40 px-6 py-2.5 text-center font-title text-base text-primary/70 uppercase tracking-[0.12em] [animation:landingFadeIn_700ms_cubic-bezier(0.22,1,0.36,1)_420ms_both] md:min-h-14 md:px-10 md:text-xl',
    applySubLabel:
      'mt-0.5 font-mono text-[9px] text-primary/75 uppercase tracking-[0.08em] md:text-[11px]',
    chevron:
      'font-title text-3xl text-background/85 leading-none [animation:bounce_1.5s_ease-in-out_infinite]',
    peopleSection:
      'selection-on-light flex h-auto min-h-0 w-full items-start justify-center bg-background px-6 py-10 text-primary sm:px-10 md:h-dvh md:items-center md:py-0',
    marqueeHeading:
      'm-0 whitespace-nowrap font-bold font-title text-4xl text-primary uppercase leading-none sm:text-6xl md:text-7xl lg:text-8xl',
    marqueeTrack:
      'flex w-max flex-nowrap items-center gap-5 whitespace-nowrap px-4 font-bold font-title text-primary uppercase leading-none tracking-[0.08em]',
    marqueeChip: undefined,
    marqueeOutline: OUTLINE_STYLE,
    sponsorSection:
      'selection-on-light flex w-full items-center justify-center bg-background px-6 py-16 text-primary sm:px-10 md:h-dvh md:py-0',
    sponsorHeading:
      'mt-4 font-bold font-title text-5xl leading-tight sm:text-6xl md:text-7xl',
    featuredRow:
      'flex transform-gpu justify-center transition duration-700 ease-out will-change-transform',
    tourGrid: 'grid gap-8 md:grid-cols-3 lg:grid-cols-6',
    tourItem:
      'flex transform-gpu items-center justify-center transition duration-700 ease-out will-change-transform',
    arcadeSection: 'relative min-h-svh w-full overflow-hidden bg-background',
  },
};

type Sponsor = {
  from: 'left' | 'right';
  name: string;
  src: string;
  sizeClass?: string;
};

type LandingEdition = {
  variant: LandingVariant;
  city: string;
  heroDates: string;
  totalHackers: number;
  applyHref: string;
  applyLabel: string;
  applyClosedLabel: string;
  applicationsClosed: boolean;
  heroModel: 'wire' | 'mate' | 'skull' | 'yare' | 'parrot' | 'telescope';
  featured: Array<{ name: string; src: string; className: string }>;
  tourSponsors: Sponsor[];
  arcadePartnerLogo: string | null;
};

const FEATURED_WIDE = 'h-16 w-full max-w-[640px] bg-primary sm:h-20 md:h-24';
const FEATURED_NARROW = 'h-16 w-full max-w-[320px] bg-primary sm:h-20 md:h-24';

const EDITIONS: Record<string, LandingEdition> = {
  '26-ar': {
    variant: 'ar',
    city: 'buenos aires',
    heroDates: '8 al 10 de mayo',
    totalHackers: 110,
    applyHref: '/26-ar/apply',
    applyLabel: 'postulá con tu equipo',
    applyClosedLabel: 'postulaciones cerradas',
    // The Buenos Aires page-client carries no countdown at all: the button is disabled
    // in the markup, so the deadlines never reach the DOM there.
    applicationsClosed: true,
    heroModel: 'mate',
    featured: [
      {
        name: 'Anthropic',
        src: '/assets/logos/anthropic.svg',
        className: 'h-16 w-full max-w-[900px] bg-primary sm:h-20 md:h-24',
      },
    ],
    tourSponsors: [
      { from: 'left', name: 'Profound', src: '/assets/logos/profound.svg' },
      { from: 'right', name: 'Supabase', src: '/assets/logos/supabase.svg' },
      { from: 'left', name: 'Vercel', src: '/assets/logos/vercel.svg' },
      { from: 'right', name: 'ElevenLabs', src: '/assets/logos/elevenlabs.svg' },
      { from: 'left', name: 'Kapso', src: '/assets/logos/kapso-padded.svg' },
      { from: 'right', name: 'Kairos', src: '/assets/logos/kairos.png' },
    ],
    arcadePartnerLogo: '/assets/logos/paisanos.svg',
  },
  '26-co': {
    variant: 'ph26',
    city: 'bogotá',
    heroDates: '21 al 23 de agosto',
    totalHackers: 100,
    applyHref: '/26-co/apply',
    applyLabel: 'aplica con tu equipo',
    applyClosedLabel: 'aplicaciones cerradas',
    applicationsClosed: false,
    heroModel: 'wire',
    featured: [
      {
        name: 'Anthropic',
        src: '/assets/logos/anthropic.svg',
        className: FEATURED_WIDE,
      },
      {
        name: 'Buk',
        src: '/assets/logos/buk-crop.svg',
        className: FEATURED_NARROW,
      },
    ],
    tourSponsors: [
      { from: 'left', name: 'Supabase', src: '/assets/logos/supabase.svg' },
      { from: 'left', name: 'ElevenLabs', src: '/assets/logos/elevenlabs.svg' },
      { from: 'right', name: 'Kapso', src: '/assets/logos/kapso-padded.svg' },
      { from: 'right', name: 'Vercel', src: '/assets/logos/vercel.svg' },
      { from: 'left', name: 'Render', src: '/assets/logos/render.svg' },
    ],
    arcadePartnerLogo: null,
  },
  '26-ve': {
    variant: 'ph26',
    city: 'caracas',
    heroDates: '23 al 25 de octubre',
    totalHackers: 100,
    applyHref: '/26-ve/apply',
    applyLabel: 'aplica con tu equipo',
    applyClosedLabel: 'aplicaciones cerradas',
    applicationsClosed: false,
    heroModel: 'parrot',
    featured: [
      {
        name: 'Anthropic',
        src: '/assets/logos/anthropic.svg',
        className: FEATURED_WIDE,
      },
      {
        name: 'Yummy',
        src: '/assets/logos/yummy.svg',
        className: FEATURED_NARROW,
      },
    ],
    tourSponsors: [
      { from: 'left', name: 'Cashea', src: '/assets/logos/cashea.svg' },
      { from: 'left', name: 'ElevenLabs', src: '/assets/logos/elevenlabs.svg' },
      { from: 'right', name: 'Kapso', src: '/assets/logos/kapso-padded.svg' },
      { from: 'right', name: 'Vercel', src: '/assets/logos/vercel.svg' },
      { from: 'left', name: 'Render', src: '/assets/logos/render.svg' },
    ],
    arcadePartnerLogo: null,
  },
  // Started from Caracas' page, with the ELT on Cerro Armazones in the hero instead of
  // the parrot. Applies through the shared 26-* flow at /26-cl/apply.
  '26-cl': {
    variant: 'ph26',
    city: 'santiago',
    heroDates: '20 al 22 de noviembre',
    totalHackers: 200,
    applyHref: '/26-cl/apply',
    applyLabel: 'postula con tu equipo',
    applyClosedLabel: 'aplicaciones cerradas',
    applicationsClosed: false,
    heroModel: 'telescope',
    featured: [
      {
        name: 'Anthropic',
        src: '/assets/logos/anthropic.svg',
        className: FEATURED_WIDE,
      },
      {
        // buk.cl's own header logo (hubfs/buk-accent.svg), drawn as a mask like the rest
        name: 'Buk',
        src: '/assets/logos/buk.svg',
        className: FEATURED_NARROW,
      },
    ],
    tourSponsors: [
      { from: 'left', name: 'ElevenLabs', src: '/assets/logos/elevenlabs.svg' },
      { from: 'right', name: 'Kapso', src: '/assets/logos/kapso-padded.svg' },
      { from: 'right', name: 'Vercel', src: '/assets/logos/vercel.svg' },
      { from: 'left', name: 'Render', src: '/assets/logos/render.svg' },
    ],
    arcadePartnerLogo: null,
  },
  '26-mx': {
    variant: 'ph26',
    city: 'ciudad de méxico',
    heroDates: '19 al 21 de junio',
    totalHackers: 100,
    applyHref: '/26-mx/apply',
    applyLabel: 'aplica con tu equipo',
    applyClosedLabel: 'aplicaciones cerradas',
    applicationsClosed: false,
    heroModel: 'skull',
    featured: [
      {
        name: 'Anthropic',
        src: '/assets/logos/anthropic.svg',
        className: FEATURED_WIDE,
      },
      {
        name: 'Firecrawl',
        src: '/assets/logos/firecrawl.svg',
        className: FEATURED_NARROW,
      },
    ],
    tourSponsors: [
      { from: 'left', name: 'Supabase', src: '/assets/logos/supabase.svg' },
      { from: 'left', name: 'Render', src: '/assets/logos/render.svg' },
      { from: 'left', name: 'ElevenLabs', src: '/assets/logos/elevenlabs.svg' },
      { from: 'right', name: 'Kapso', src: '/assets/logos/kapso-padded.svg' },
      { from: 'left', name: 'altur', src: '/assets/logos/altur.svg' },
      {
        from: 'right',
        name: 'Stripe',
        src: '/assets/logos/stripe.svg',
        sizeClass: 'h-10 sm:h-12 md:h-14',
      },
      { from: 'right', name: 'Vercel', src: '/assets/logos/vercel.svg' },
      { from: 'left', name: 'Fintual', src: '/assets/logos/fintual-mono.svg' },
      { from: 'left', name: 'Kairos', src: '/assets/logos/kairos.png' },
    ],
    arcadePartnerLogo: null,
  },
};

function hackerIcons(total: number) {
  return Array.from({ length: total }, (_, index) => ({
    id: index + 1,
    gender: index % 5 === 4 ? 'woman' : 'man',
  }));
}

const TOTAL_CLOCKS = 120;
const MAX_HOURS = 36;
const CLOCK_VARIANTS = Array.from({ length: TOTAL_CLOCKS }, (_, index) => {
  const progress = index / (TOTAL_CLOCKS - 1);
  const elapsedMinutes = progress * MAX_HOURS * 60;
  const minuteAngle = (elapsedMinutes % 60) * 6;
  const hourAngle = ((elapsedMinutes / 60) % 12) * 30;

  return {
    hourAngle,
    id: index + 1,
    minuteAngle,
  };
});
const CLOCK_FLOW = [...CLOCK_VARIANTS].reverse();
const ONLY_CRACKS_REPEAT = 50;
// `decorated` is the variant's emphasis: a filled chip under `theme-ph26`, outlined text
// in Buenos Aires. Every other item, in both.
const ONLY_CRACKS_ITEMS = Array.from(
  { length: ONLY_CRACKS_REPEAT },
  (_, index) => ({
    id: index + 1,
    decorated: index % 2 === 1,
  }),
);
const TEAM_SIZE_REPEAT = 44;
const TEAM_SIZE_CENTER_ID = Math.floor(TEAM_SIZE_REPEAT / 2) + 1;
// Same idea, mirrored: `theme-ph26` fills every other phrase and Buenos Aires outlines
// everything *but* the centred one, which is the phrase the scroll animation lands on.
function teamSizeItems(variant: LandingVariant) {
  return Array.from({ length: TEAM_SIZE_REPEAT }, (_, index) => {
    const id = index + 1;
    const isCenter = id === TEAM_SIZE_CENTER_ID;

    return {
      id,
      isCenter,
      decorated: variant === 'ar' ? !isCenter : index % 2 === 1,
    };
  });
}
const SLIDE_TEXT_SIZE_CLASS = 'text-2xl sm:text-3xl md:text-5xl lg:text-6xl';
const EMPTY_COUNTDOWN = {
  days: '00',
  hours: '00',
  minutes: '00',
  seconds: '00',
};

function getCountdownFromDiff(diffMs: number) {
  const safeDiff = Math.max(0, diffMs);
  const days = Math.floor(safeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((safeDiff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((safeDiff / (1000 * 60)) % 60);
  const seconds = Math.floor((safeDiff / 1000) % 60);

  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
  };
}

function getLogoMaskStyle(src: string): CSSProperties {
  return {
    WebkitMaskImage: `url(${src})`,
    WebkitMaskPosition: 'center',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskSize: 'contain',
    maskImage: `url(${src})`,
    maskPosition: 'center',
    maskRepeat: 'no-repeat',
    maskSize: 'contain',
  };
}

function HeroModel({
  model,
  topAnchor,
  bottomAnchor,
  onEyepieceChange,
  onTitle,
}: {
  model: LandingEdition['heroModel'];
  topAnchor: RefObject<HTMLElement | null>;
  bottomAnchor: RefObject<HTMLElement | null>;
  onEyepieceChange: (open: boolean) => void;
  onTitle: () => void;
}) {
  if (model === 'telescope') return <TelescopeHero onEyepieceChange={onEyepieceChange} title={topAnchor} onTitle={onTitle} />;
  if (model === 'mate') return <RotatingMate />;
  if (model === 'skull') return <RotatingSkull />;
  if (model === 'yare') return <YareMask />;
  if (model === 'parrot') return <ParrotFiligree topAnchor={topAnchor} bottomAnchor={bottomAnchor} />;
  return <WireBus />;
}

export default function Show({
  title,
  description,
  canonicalUrl,
  markdownUrl,
  editionSlug,
  priorityDeadlineAt,
  regularDeadlineAt,
  mentors,
  scheduleDays,
  scheduleDateLine,
  scheduleLocale,
  timeZone,
}: LandingsShow) {
  const edition = EDITIONS[editionSlug];
  const styles = STYLES[edition.variant];
  const hackerIconsForEdition = hackerIcons(edition.totalHackers);
  const teamSizeItemsForEdition = teamSizeItems(edition.variant);
  const peopleSectionRef = useRef<HTMLElement>(null);
  const peopleTrackRef = useRef<HTMLDivElement>(null);
  const peopleRowRef = useRef<HTMLDivElement>(null);
  const peopleStartedRef = useRef(false);
  const peopleFinishedRef = useRef(false);
  const cracksTrackRef = useRef<HTMLDivElement>(null);
  const cracksFinalRef = useRef<HTMLSpanElement>(null);
  const cracksStartedRef = useRef(false);
  const cracksFinishedRef = useRef(false);
  const teamSizeTrackRef = useRef<HTMLDivElement>(null);
  const teamSizeCenterRef = useRef<HTMLSpanElement>(null);
  const teamSizeStartedRef = useRef(false);
  const teamSizeFinishedRef = useRef(false);
  const clockSectionRef = useRef<HTMLDivElement>(null);
  const clockTrackRef = useRef<HTMLDivElement>(null);
  const clockRowRef = useRef<HTMLDivElement>(null);
  const clockLabelRef = useRef<HTMLParagraphElement>(null);
  const firstClockRef = useRef<HTMLDivElement>(null);
  const clockStartedRef = useRef(false);
  const clockFinishedRef = useRef(false);
  const sponsorSectionRef = useRef<HTMLElement>(null);
  const arcadeSectionRef = useRef<HTMLElement>(null);
  const arcadeScrollProgressRef = useRef(0);
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const countdownSlideRef = useRef<HTMLElement>(null);
  const [showCountdownEnter, setShowCountdownEnter] = useState(false);
  const [showSponsors, setShowSponsors] = useState(false);
  const [countdown, setCountdown] = useState({ ...EMPTY_COUNTDOWN });
  const [priorityCountdown, setPriorityCountdown] = useState({
    ...EMPTY_COUNTDOWN,
  });
  const [isPriorityClosed, setIsPriorityClosed] = useState(false);
  const [isFinalClosed, setIsFinalClosed] = useState(false);
  // The telescope hero takes over the whole fold while you look through the eyepiece.
  const [eyepieceOpen, setEyepieceOpen] = useState(false);
  const heroChromeClass = `transition-opacity duration-500 ${eyepieceOpen ? 'pointer-events-none opacity-0' : 'opacity-100'}`;
  // The telescope's lasers write the title: until they are done (TelescopeHero's onTitle)
  // the real logo, the place and dates and the button stay hidden — from the server-rendered
  // first paint on, so none of them flashes before the scene is up. Then they fade in, in
  // that order.
  const writesTitle = edition.heroModel === 'telescope';
  const [titleWritten, setTitleWritten] = useState(!writesTitle);
  const revealClass = (delay: string) =>
    writesTitle
      ? `transition-opacity duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${delay} ${titleWritten ? 'opacity-100' : 'pointer-events-none opacity-0'}`
      : '';

  useEffect(() => {
    const section = countdownSlideRef.current;

    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        setShowCountdownEnter(true);
        observer.disconnect();
      },
      { threshold: 0.45 },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, [editionSlug]);


  useEffect(() => {
    // A stop with no deadlines yet (Santiago) has nothing to count down to.
    if (!priorityDeadlineAt || !regularDeadlineAt) return;
    const priorityTarget = new Date(priorityDeadlineAt).getTime();
    const regularTarget = new Date(regularDeadlineAt).getTime();

    const updateCountdown = () => {
      const now = Date.now();

      const priorityClosed = now > priorityTarget;
      const finalClosed = now > regularTarget;
      setIsPriorityClosed(priorityClosed);
      setIsFinalClosed(finalClosed);

      if (finalClosed) {
        setCountdown({ ...EMPTY_COUNTDOWN });
        setPriorityCountdown({ ...EMPTY_COUNTDOWN });
        return;
      }

      setPriorityCountdown(
        priorityClosed
          ? { ...EMPTY_COUNTDOWN }
          : getCountdownFromDiff(priorityTarget - now),
      );
      const target = priorityClosed ? regularTarget : priorityTarget;
      setCountdown(getCountdownFromDiff(target - now));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(intervalId);
  }, [priorityDeadlineAt, regularDeadlineAt]);

  useEffect(() => {
    const section = peopleSectionRef.current;
    const track = peopleTrackRef.current;
    const peopleRow = peopleRowRef.current;

    if (!section || !track || !peopleRow) return;

    let frameId: number | null = null;
    let startX = 0;
    let endX = 0;

    const setTrackPosition = (x: number, opacity: number) => {
      track.style.transform = `translate3d(${x}px, 0, 0)`;
      track.style.opacity = String(opacity);
    };

    const getEndX = () => {
      const sectionWidth = section.clientWidth;
      const peopleWidth = peopleRow.scrollWidth;
      const trackWidth = track.scrollWidth;
      const anchorPeopleToMiddle = sectionWidth * 0.5 - peopleWidth;
      const keepTextVisible = sectionWidth - trackWidth - 56;
      return Math.min(anchorPeopleToMiddle, keepTextVisible);
    };

    const recalculatePositions = () => {
      const sectionWidth = section.clientWidth;
      startX = sectionWidth + 96;
      endX = getEndX();

      if (!peopleStartedRef.current) {
        setTrackPosition(startX, 0.12);
      } else if (peopleFinishedRef.current) {
        setTrackPosition(endX, 1);
      }
    };

    const runAnimation = () => {
      recalculatePositions();
      const startTime = performance.now();
      // a longer crowd gets a little longer to pass, so it doesn't rush by: 1.9s at 100
      const durationMs = 1900 * Math.sqrt(Math.max(1, peopleRow.childElementCount / 100));

      const tick = (now: number) => {
        const progress = Math.min(1, (now - startTime) / durationMs);
        const eased = 1 - (1 - progress) ** 4;
        endX = getEndX();
        const x = startX + (endX - startX) * eased;
        const opacity = Math.min(1, 0.12 + progress * 1.35);
        setTrackPosition(x, opacity);

        if (progress < 1) {
          frameId = window.requestAnimationFrame(tick);
          return;
        }

        peopleFinishedRef.current = true;
      };

      frameId = window.requestAnimationFrame(tick);
    };

    recalculatePositions();

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);

        if (!isVisible || peopleStartedRef.current) return;

        peopleStartedRef.current = true;
        observer.disconnect();
        runAnimation();
      },
      { threshold: 0.4 },
    );

    observer.observe(section);

    const onResize = () => {
      recalculatePositions();
    };

    window.addEventListener('resize', onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    const section = clockSectionRef.current;
    const track = clockTrackRef.current;
    const clockRow = clockRowRef.current;
    const label = clockLabelRef.current;
    const firstClock = firstClockRef.current;

    if (!section || !track || !clockRow || !label || !firstClock) return;

    let frameId: number | null = null;
    let startX = 0;
    let endX = 0;

    const setTrackPosition = (x: number, opacity: number) => {
      track.style.transform = `translate3d(${x}px, 0, 0)`;
      track.style.opacity = String(opacity);
    };

    const getEndX = () => {
      const targetPadding = 24;
      return targetPadding - label.offsetLeft;
    };

    const recalculatePositions = () => {
      const targetPadding = 24;
      const firstClockLeft = clockRow.offsetLeft + firstClock.offsetLeft;
      startX = targetPadding - firstClockLeft;
      endX = getEndX();

      if (!clockStartedRef.current) {
        setTrackPosition(startX, 0.12);
      } else if (clockFinishedRef.current) {
        setTrackPosition(endX, 1);
      }
    };

    const runAnimation = () => {
      recalculatePositions();
      const startTime = performance.now();
      const durationMs = 1950;

      const tick = (now: number) => {
        const progress = Math.min(1, (now - startTime) / durationMs);
        const eased = 1 - (1 - progress) ** 4;
        endX = getEndX();
        const x = startX + (endX - startX) * eased;
        const opacity = Math.min(1, 0.12 + progress * 1.35);
        setTrackPosition(x, opacity);

        if (progress < 1) {
          frameId = window.requestAnimationFrame(tick);
          return;
        }

        clockFinishedRef.current = true;
      };

      frameId = window.requestAnimationFrame(tick);
    };

    recalculatePositions();

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);

        if (!isVisible || clockStartedRef.current) return;

        clockStartedRef.current = true;
        observer.disconnect();
        runAnimation();
      },
      { threshold: 0.4 },
    );

    observer.observe(section);

    const onResize = () => {
      recalculatePositions();
    };

    window.addEventListener('resize', onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    const section = peopleSectionRef.current;
    const track = cracksTrackRef.current;
    const finalPhrase = cracksFinalRef.current;

    if (!section || !track || !finalPhrase) return;

    let frameId: number | null = null;
    let startX = 0;
    let endX = 0;

    const setTrackPosition = (x: number, opacity: number) => {
      track.style.transform = `translate3d(${x}px, 0, 0)`;
      track.style.opacity = String(opacity);
    };

    const getEndX = () => {
      return -finalPhrase.offsetLeft;
    };

    const recalculatePositions = () => {
      const sectionWidth = section.clientWidth;
      startX = sectionWidth + 96;
      endX = getEndX();

      if (!cracksStartedRef.current) {
        setTrackPosition(startX, 0.12);
      } else if (cracksFinishedRef.current) {
        setTrackPosition(endX, 1);
      }
    };

    const runAnimation = () => {
      recalculatePositions();
      const startTime = performance.now();
      const durationMs = 1900;

      const tick = (now: number) => {
        const progress = Math.min(1, (now - startTime) / durationMs);
        const eased = 1 - (1 - progress) ** 4;
        endX = getEndX();
        const x = startX + (endX - startX) * eased;
        const opacity = Math.min(1, 0.12 + progress * 1.35);
        setTrackPosition(x, opacity);

        if (progress < 1) {
          frameId = window.requestAnimationFrame(tick);
          return;
        }

        cracksFinishedRef.current = true;
      };

      frameId = window.requestAnimationFrame(tick);
    };

    recalculatePositions();

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);

        if (!isVisible || cracksStartedRef.current) return;

        cracksStartedRef.current = true;
        observer.disconnect();
        runAnimation();
      },
      { threshold: 0.4 },
    );

    observer.observe(section);

    const onResize = () => {
      recalculatePositions();
    };

    window.addEventListener('resize', onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    const section = peopleSectionRef.current;
    const track = teamSizeTrackRef.current;
    const centeredPhrase = teamSizeCenterRef.current;

    if (!section || !track || !centeredPhrase) return;

    let frameId: number | null = null;
    let startX = 0;
    let endX = 0;

    const setTrackPosition = (x: number, opacity: number) => {
      track.style.transform = `translate3d(${x}px, 0, 0)`;
      track.style.opacity = String(opacity);
    };

    const getEndX = () => {
      const sectionWidth = section.clientWidth;
      const rightPadding = Math.max(16, sectionWidth * 0.05);
      const phraseRight =
        centeredPhrase.offsetLeft + centeredPhrase.offsetWidth;
      return sectionWidth - rightPadding - phraseRight;
    };

    const recalculatePositions = () => {
      startX = -track.scrollWidth - 96;
      endX = getEndX();

      if (!teamSizeStartedRef.current) {
        setTrackPosition(startX, 0.12);
      } else if (teamSizeFinishedRef.current) {
        setTrackPosition(endX, 1);
      }
    };

    const runAnimation = () => {
      recalculatePositions();
      const startTime = performance.now();
      const durationMs = 1900;

      const tick = (now: number) => {
        const progress = Math.min(1, (now - startTime) / durationMs);
        const eased = 1 - (1 - progress) ** 4;
        endX = getEndX();
        const x = startX + (endX - startX) * eased;
        const opacity = Math.min(1, 0.12 + progress * 1.35);
        setTrackPosition(x, opacity);

        if (progress < 1) {
          frameId = window.requestAnimationFrame(tick);
          return;
        }

        teamSizeFinishedRef.current = true;
      };

      frameId = window.requestAnimationFrame(tick);
    };

    recalculatePositions();

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);

        if (!isVisible || teamSizeStartedRef.current) return;

        teamSizeStartedRef.current = true;
        observer.disconnect();
        runAnimation();
      },
      { threshold: 0.4 },
    );

    observer.observe(section);

    const onResize = () => {
      recalculatePositions();
    };

    window.addEventListener('resize', onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    const section = sponsorSectionRef.current;

    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        if (!isVisible) return;

        setShowSponsors(true);
        observer.disconnect();
      },
      { threshold: 0.35 },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const section = arcadeSectionRef.current;
      if (!section) return;
      // 0 when section bottom enters viewport, 1 when section top reaches viewport top
      const rect = section.getBoundingClientRect();
      // The layout viewport's height, not `innerHeight`: on phones the latter grows and
      // shrinks with the browser's toolbar mid-scroll, which made the camera jump.
      const progress = 1 - rect.top / document.documentElement.clientHeight;
      arcadeScrollProgressRef.current = Math.max(0, Math.min(1, progress));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const getSponsorMotionClass = (from: 'left' | 'right') =>
    showSponsors
      ? 'translate-x-0 rotate-0 opacity-100'
      : `${from === 'left' ? '-translate-x-24 -rotate-6' : 'translate-x-24 rotate-6'} opacity-0`;

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {(editionSlug === '26-ve' || editionSlug === '26-cl') && (
          <meta property="og:image" content={`https://hack.platan.us/assets/og/${editionSlug}.png`} />
        )}
        {(editionSlug === '26-ve' || editionSlug === '26-cl') && (
          <meta name="twitter:image" content={`https://hack.platan.us/assets/og/${editionSlug}.png`} />
        )}
        <link rel="canonical" href={canonicalUrl} />
        {markdownUrl ? (
          <link rel="alternate" type="text/markdown" href={markdownUrl} />
        ) : null}
      </Head>
      <section className={styles.hero}>
        <HeroModel
          model={edition.heroModel}
          topAnchor={heroTitleRef}
          bottomAnchor={heroCtaRef}
          onEyepieceChange={setEyepieceOpen}
          onTitle={() => setTitleWritten(true)}
        />
        <h1 ref={heroTitleRef} className={`absolute inset-x-0 top-10 z-10 px-4 text-center lowercase tracking-tighter md:top-14 ${heroChromeClass}`}>
          <Link
            href="/"
            className="inline-block transition-opacity hover:opacity-85"
          >
            {/* a touch larger over the telescope, whose open sky leaves the room for it */}
            <span
              className={`whitespace-nowrap font-logo ${
                edition.heroModel === 'telescope'
                  ? 'text-[min(2.55rem,10.75vw)] sm:text-[3.4rem] md:text-[5.25rem] lg:text-[6.75rem]'
                  : 'text-[min(2.25rem,9.5vw)] sm:text-5xl md:text-7xl lg:text-8xl'
              } ${revealClass('delay-0')}`}
            >
              <span className="font-light">platanus hack</span>{' '}
              <span className="font-medium">[26]</span>
            </span>{' '}
            {/* Two lines on phones (city, then dates) rather than letting the dates wrap
                mid-phrase; one line from `sm` up. */}
            <span className={`mt-3 flex flex-col items-center gap-1 font-title text-sm uppercase tracking-[0.18em] sm:mt-2 sm:block sm:text-base sm:tracking-[0.2em] md:text-xl ${revealClass('delay-[650ms]')}`}>
              <span>
                latam tour: {edition.city}
                <span className="hidden sm:inline"> - </span>
              </span>{' '}
              <strong className={styles.heroDates}>{edition.heroDates}</strong>
            </span>
          </Link>
        </h1>
        <div ref={heroCtaRef} className={`absolute inset-x-0 bottom-8 z-10 flex justify-center px-4 md:bottom-12 ${heroChromeClass}`}>
          <div className={revealClass('delay-[1300ms]')}>
          {edition.applicationsClosed || isFinalClosed ? (
            <button
              type="button"
              aria-disabled="true"
              tabIndex={-1}
              disabled
              className={styles.applyButton}
            >
              <span>{edition.applyLabel}</span>
              <span className={styles.applySubLabel}>
                {edition.applyClosedLabel}
              </span>
            </button>
          ) : (
            <Link
              href={edition.applyHref}
              className="inline-flex min-h-12 flex-col items-center justify-center border-2 border-primary bg-primary px-6 py-2.5 text-center font-title text-background text-base uppercase tracking-[0.12em] transition [animation:landingFadeIn_700ms_cubic-bezier(0.22,1,0.36,1)_420ms_both] hover:scale-[1.02] md:min-h-14 md:px-10 md:text-xl"
            >
              <span>{edition.applyLabel}</span>
              <span className="mt-0.5 font-mono text-[9px] text-background/75 uppercase tracking-[0.08em] md:text-[11px]">
                {isPriorityClosed
                  ? `general ${countdown.days}:${countdown.hours}:${countdown.minutes}:${countdown.seconds}`
                  : `priority ${priorityCountdown.days}:${priorityCountdown.hours}:${priorityCountdown.minutes}:${priorityCountdown.seconds}`}
              </span>
            </Link>
          )}
          </div>
        </div>
        <div className={`pointer-events-none absolute inset-x-0 bottom-1 z-10 flex justify-center ${heroChromeClass}`}>
          <span className={styles.chevron}>⌄</span>
        </div>
      </section>

      <section ref={peopleSectionRef} className={styles.peopleSection}>
        <div className="mx-auto flex w-full max-w-[1800px] flex-col justify-start gap-3 py-2 md:h-full md:justify-evenly md:gap-0 md:py-8">
          <div className="relative h-36 overflow-hidden md:h-[14rem]">
            <div className="-translate-y-1/2 absolute top-1/2">
              <div
                ref={peopleTrackRef}
                className="flex w-max flex-nowrap items-center gap-12 px-4 will-change-transform"
                style={{ transform: 'translate3d(110vw, 0, 0)', opacity: 0.12 }}
              >
                <div
                  ref={peopleRowRef}
                  className="flex flex-nowrap items-center gap-4 md:gap-5"
                >
                  {hackerIconsForEdition.map(({ id, gender }) => {
                    const Icon = gender === 'man' ? FaPerson : FaPersonDress;

                    return (
                      <Icon
                        key={id}
                        className="size-14 shrink-0 text-primary sm:size-16 md:size-20"
                      />
                    );
                  })}
                </div>

                <p className={styles.marqueeHeading}>
                  {edition.totalHackers} hackers
                </p>
              </div>
            </div>
          </div>

          <div
            ref={clockSectionRef}
            className="relative h-36 overflow-hidden bg-background md:h-[14rem]"
          >
            <div className="-translate-y-1/2 absolute top-1/2">
              <div
                ref={clockTrackRef}
                className="flex w-max flex-nowrap items-center gap-8 px-4 will-change-transform md:gap-10"
                style={{
                  transform: 'translate3d(-110vw, 0, 0)',
                  opacity: 0.12,
                }}
              >
                <p
                  ref={clockLabelRef}
                  className={styles.marqueeHeading}
                >
                  36 horas
                </p>
                <div
                  ref={clockRowRef}
                  className="flex flex-nowrap items-center gap-3 md:gap-4"
                >
                  {CLOCK_FLOW.map(({ id, hourAngle, minuteAngle }) => {
                    const isFirstClock = id === 1;

                    return (
                      <div
                        key={id}
                        ref={isFirstClock ? firstClockRef : undefined}
                        className="relative size-14 shrink-0 rounded-full border-[3px] border-primary sm:size-16 md:size-20"
                      >
                        <span
                          className="absolute top-1/2 left-1/2 h-[26%] w-[3px] origin-bottom rounded-full bg-primary"
                          style={{
                            transform: `translate(-50%, -100%) rotate(${hourAngle}deg)`,
                          }}
                        />
                        <span
                          className="absolute top-1/2 left-1/2 h-[38%] w-[2px] origin-bottom rounded-full bg-primary"
                          style={{
                            transform: `translate(-50%, -100%) rotate(${minuteAngle}deg)`,
                          }}
                        />
                        <span className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 size-2 rounded-full bg-primary" />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="relative h-20 overflow-hidden md:h-[7rem]">
            <div className="-translate-y-1/2 absolute top-1/2">
              <div
                ref={teamSizeTrackRef}
                className={`${styles.marqueeTrack} ${SLIDE_TEXT_SIZE_CLASS}`}
                style={{ transform: 'translate3d(110vw, 0, 0)', opacity: 0.12 }}
              >
                {teamSizeItemsForEdition.map(({ id, isCenter, decorated }) => (
                  <span
                    key={id}
                    ref={isCenter ? teamSizeCenterRef : undefined}
                    className={decorated ? styles.marqueeChip : undefined}
                    style={decorated ? styles.marqueeOutline : undefined}
                  >
                    3-5 por equipo
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="relative hidden h-24 overflow-hidden md:block md:h-[7rem]">
            <div className="-translate-y-1/2 absolute top-1/2">
              <div
                ref={cracksTrackRef}
                className={`${styles.marqueeTrack} ${SLIDE_TEXT_SIZE_CLASS}`}
                style={{ transform: 'translate3d(110vw, 0, 0)', opacity: 0.12 }}
              >
                {ONLY_CRACKS_ITEMS.map(({ id, decorated }) => (
                  <span
                    key={id}
                    className={decorated ? styles.marqueeChip : undefined}
                    style={decorated ? styles.marqueeOutline : undefined}
                  >
                    only cracks
                  </span>
                ))}
                <span ref={cracksFinalRef} className="whitespace-nowrap">
                  only cracks @ {edition.city}
                </span>
                <span className="whitespace-nowrap" style={OUTLINE_STYLE}>
                  only cracks @ {edition.city}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {editionSlug === '26-cl' ? (
        <>
          {/* "$7,000 y pasajes a…" — and the very next section says where: San Francisco */}
          <SantiagoPrizeSlide />
          <GoldenGateSection />
        </>
      ) : (
        <PrizeSlide variant={edition.variant} />
      )}

      <section ref={sponsorSectionRef} className={styles.sponsorSection}>
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center text-center">
          <h2 className={styles.sponsorHeading}>
            <span className="inline-block bg-primary px-3 py-1 text-background sm:px-4 sm:py-1.5">
              sponsored by the big ones
            </span>
          </h2>

          <div className="mt-12 w-full">
            <div className="relative overflow-hidden py-4 sm:py-6">
              <div className="relative flex flex-col gap-8 md:gap-10">
                <div
                  className={`${styles.featuredRow} ${getSponsorMotionClass('left')}`}
                  style={{ transitionDelay: '80ms' }}
                >
                  {edition.featured.map((logo) => (
                    <div
                      key={logo.name}
                      role="img"
                      aria-label={logo.name}
                      className={logo.className}
                      style={getLogoMaskStyle(logo.src)}
                    />
                  ))}
                </div>

                <div className={styles.tourGrid}>
                  {edition.tourSponsors.map((sponsor, index) => (
                    <div
                      key={sponsor.name}
                      className={`${styles.tourItem} ${getSponsorMotionClass(sponsor.from)}`}
                      style={{
                        transitionDelay: `${160 + index * 90}ms`,
                      }}
                    >
                      <div
                        role="img"
                        aria-label={sponsor.name}
                        className={`w-full max-w-[320px] bg-primary ${sponsor.sizeClass ?? 'h-14 sm:h-16 md:h-20'}`}
                        style={getLogoMaskStyle(sponsor.src)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <Link
              href="/tour/sponsor"
              className="inline-flex min-h-12 items-center justify-center border-2 border-primary bg-primary px-6 py-2.5 text-center font-title text-background text-base uppercase tracking-[0.12em] transition hover:scale-[1.02] md:min-h-14 md:px-10 md:text-xl"
            >
              sé sponsor
            </Link>
          </div>
        </div>
      </section>

      <DualPhotoSlide variant={edition.variant} />


      {/* Arcade showcase — camera zoom driven by scroll, no hijacking */}
      <section ref={arcadeSectionRef} className={styles.arcadeSection}>
        <ArcadeShowcase
          scrollProgressRef={arcadeScrollProgressRef}
          onStartPress={() => {}}
          editionSlug={editionSlug}
          partnerLogo={edition.arcadePartnerLogo}
        />
      </section>

      <MentorsSlide mentors={mentors} variant={edition.variant} />

      <ScheduleSlide
        dark={editionSlug === '26-ve' || editionSlug === '26-cl'}
        days={scheduleDays}
        dateLine={scheduleDateLine}
        timeZone={timeZone}
        locale={scheduleLocale}
        variant={edition.variant}
      />

      {/* the closing countdown slide: Caracas, and Santiago once its deadlines are set.
          Caracas closes on the light (yellow) palette; Santiago keeps its night to the end. */}
      {(editionSlug === '26-ve' || (editionSlug === '26-cl' && priorityDeadlineAt && regularDeadlineAt)) && (
        <section
          ref={countdownSlideRef}
          className={`${editionSlug === '26-cl' ? 'theme-ph26' : 'theme-ph26-light'} selection-on-light relative flex min-h-svh w-full items-center justify-center overflow-hidden bg-background px-6 py-16 text-primary md:h-dvh md:py-0`}
        >
          <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col items-center text-center">
            <h2 className="mt-4 font-title text-5xl uppercase leading-tight sm:text-6xl md:text-7xl">
              <span className="inline-block bg-primary px-3 py-1 text-background sm:px-4 sm:py-1.5">
                the clock is ticking
              </span>
            </h2>

            <div className="mt-6 flex w-full max-w-5xl items-start justify-center gap-1 sm:gap-2 md:gap-3">
              {(
                [
                  { label: 'días', value: countdown.days, delay: '0ms' },
                  { label: 'horas', value: countdown.hours, delay: '160ms' },
                  {
                    label: 'minutos',
                    value: countdown.minutes,
                    delay: '320ms',
                  },
                  {
                    label: 'segundos',
                    value: countdown.seconds,
                    delay: '480ms',
                  },
                ] as const
              ).map((unit, index) => (
                <Fragment key={unit.label}>
                  {index > 0 && (
                    <span
                      className={`pt-1 font-title text-[clamp(1.4rem,4vw,3.2rem)] leading-none transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] sm:pt-2 ${showCountdownEnter ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
                      style={{ transitionDelay: unit.delay }}
                    >
                      :
                    </span>
                  )}
                  <div
                    className={`flex w-[23%] min-w-0 transform-gpu flex-col items-center transition-[opacity,transform,filter] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform ${showCountdownEnter ? 'translate-y-0 scale-100 opacity-100 blur-0' : 'translate-y-6 scale-95 opacity-0 blur-[2px]'}`}
                    style={{ transitionDelay: unit.delay }}
                  >
                    <span className="font-title text-[clamp(2rem,8vw,5.8rem)] tabular-nums leading-none tracking-tight">
                      {unit.value}
                    </span>
                    <span className="mt-2 font-title text-[10px] uppercase tracking-[0.16em] sm:text-xs">
                      {unit.label}
                    </span>
                  </div>
                </Fragment>
              ))}
            </div>

            {!isFinalClosed && (
              <p className="mt-4 font-sans text-sm uppercase tracking-[0.18em] sm:text-base">
                {editionSlug === '26-cl'
                  ? isPriorityClosed
                    ? 'para postular'
                    : 'para postular prioritariamente'
                  : isPriorityClosed
                    ? 'para aplicar'
                    : 'para aplicar prioritariamente'}
              </p>
            )}

            <div className="mt-9">
              {/* same rule and words as the hero's button: closed once the general deadline
                  passes, or while a stop has no apply flow yet */}
              {isFinalClosed || edition.applicationsClosed ? (
                <button
                  type="button"
                  aria-disabled="true"
                  tabIndex={-1}
                  disabled
                  className="inline-flex min-h-12 cursor-not-allowed items-center justify-center border-2 border-primary/60 bg-primary/40 px-6 py-2.5 text-center font-sans text-background/70 text-base uppercase tracking-[0.12em] md:min-h-14 md:px-10 md:text-xl"
                >
                  {isFinalClosed ? 'aplicaciones cerradas' : edition.applyClosedLabel}
                </button>
              ) : (
                <Link
                  href={edition.applyHref}
                  className="inline-flex min-h-12 items-center justify-center border-2 border-primary bg-primary px-6 py-2.5 text-center font-sans text-background text-base uppercase tracking-[0.12em] transition hover:scale-[1.02] md:min-h-14 md:px-10 md:text-xl"
                >
                  {edition.applyLabel}
                </Link>
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
